import { useMemo, useState } from "react"
import { Button, Stack } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { CustomTable } from "@/components/Tables/CustomTable"
import ConfirmDialog from "@/components/Dialog/ConfirmDialog"
import StatusBadge from "@/components/ui/badge"
import { CrudProvider } from "@/context/crudContext"
import { apiFetch } from "@/hooks/apiFetch"
import { useAppSelector } from "@/lib/hooks"
import { isAdminPortal } from "@/utilities/domain"
import { withBranchColumn } from "@/components/Reports/ReportBranchFilter"

const baseColumns = [
  {
    header: "Order",
    cell: ({ row }) => (
      <Link
        to={`/pos/website-orders/${row.original.order_id}`}
        className="text-blue-500 hover:underline whitespace-nowrap"
      >
        {row.original.order_code}
      </Link>
    ),
  },
  {
    header: "Customer",
    cell: ({ row }) => (
      <>
        <div>{row.original.customer_name}</div>
        <div className="text-xs text-gray-500">
          {row.original.customer_phone}
        </div>
      </>
    ),
  },
  {
    header: "Items",
    cell: ({ row }) =>
      row.original.items?.map((item) => (
        <div key={item.product_id}>
          {item.name} ({item.sku}) × {item.quantity}
          {item.reason ? ` — ${item.reason}` : ""}
        </div>
      )),
  },
  { header: "Note", cell: ({ row }) => row.original.notes || "—" },
  { header: "Requested", cell: ({ row }) => row.original.created_at },
  {
    header: "Status",
    cell: ({ row }) => <StatusBadge name={row.original.status_label} />,
  },
]

export default function ReturnRequestList() {
  return (
    <>
      <div className="breadcrumb-card">
        <h5>Return Requests</h5>
      </div>

      <CrudProvider>
        <ReturnRequestTable />
      </CrudProvider>
    </>
  )
}

function ReturnRequestTable() {
  const queryClient = useQueryClient()
  const permissions = useAppSelector((state) => state.auth.user?.permissions)
  // Requests are answered on the branch portals; company admins only read them.
  const canReview =
    !isAdminPortal() && permissions?.includes("sales-sales_returns-update")
  const [review, setReview] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  const columns = useMemo(() => {
    if (isAdminPortal()) {
      return withBranchColumn(baseColumns)
    }
    if (!canReview) {
      return baseColumns
    }

    return [
      ...baseColumns,
      {
        header: "Action",
        cell: ({ row }) =>
          row.original.status === "pending" ? (
            <Stack direction="row" gap={1}>
              <Button
                size="small"
                variant="contained"
                onClick={() =>
                  setReview({ action: "accept", request: row.original })
                }
              >
                Accept
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() =>
                  setReview({ action: "cancel", request: row.original })
                }
              >
                Cancel
              </Button>
            </Stack>
          ) : (
            row.original.sales_return_code || "—"
          ),
      },
    ]
  }, [canReview])

  const submitReview = async () => {
    const { action, request } = review
    setReview(null)
    setIsSaving(true)
    try {
      await apiFetch(`sales-return-requests/${request.id}/${action}`, {
        method: "POST",
      })
      toast.success(
        action === "accept" ? "Sales return created" : "Request cancelled"
      )
    } catch (error) {
      toast.error(error?.message ?? "Unable to update this request")
    } finally {
      setIsSaving(false)
      queryClient.invalidateQueries({ queryKey: ["sales-return-requests"] })
      queryClient.invalidateQueries({ queryKey: ["sales-return"] })
    }
  }

  return (
    <>
      <CustomTable
        label="Return Requests"
        columns={columns}
        apiPath="sales-return-requests"
        addBtn={false}
        action={false}
      />
      <ConfirmDialog
        open={Boolean(review) && !isSaving}
        title={
          review?.action === "accept"
            ? "Accept this return?"
            : "Cancel this request?"
        }
        description={
          review?.action === "accept"
            ? `This creates a sales return for order ${review?.request.order_code} and puts the items back in stock.`
            : `The customer will see this request for order ${review?.request.order_code} as cancelled.`
        }
        closeText="Back"
        confirmText={review?.action === "accept" ? "Accept" : "Cancel Request"}
        onClose={() => setReview(null)}
        onConfirm={submitReview}
      />
    </>
  )
}
